import _cloneDeep from "lodash-es/cloneDeep";
/* ------------------------------------------------------------------ */
/* CONSTANTS & HELPERS                                                */
/* ------------------------------------------------------------------ */

const SERVICE_MODES = [
  "personal_services",
  "table_inventory",
  "class_based",
  "accommodation",
];

const SHORT_TO_LONG_DAY = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

const LONG_DAY_TO_NUM = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

const MODIFIER_MODE_MAP = {
  percent: "percentage",
  fixed: "fixed_amount",
};

const MODIFIER_TYPE_MAP = {
  length_of_stay: "los",
};

const STRIP_KEYS = [
  "uploadFile",
  "uploadPreview",
  "uploadId",
  "photo_id",
  "photo_service_id",
  "photo_bundle_id",
  "mode", // helper injected by stampMode()
];

// For check-in / check-out display: returns "1:44 AM", "11:00 PM"
export const isoTo12hrTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
// Normalize edit_rules array shape: drop UI-only fields, ensure applies_to present for custom
function normEditRules(list = []) {
  return (list || []).map(r => {
    const out = {
      id   : r.id,
  label: (r.label || '').trim(),
  scope: r.scope === 'custom' ? 'custom' : 'all',
      max_date_shift_days      : Number.isInteger(r.max_date_shift_days) ? r.max_date_shift_days : 0,
      edit_cutoff_value        : Number.isInteger(r.edit_cutoff_value) ? r.edit_cutoff_value : 0,
      edit_cutoff_unit         : r.edit_cutoff_unit || 'hours',
      edit_fee_amount          : Number(r.edit_fee_amount || 0),
      edit_fee_type            : r.edit_fee_type || 'fixed',
      max_edits_per_reservation: Number.isInteger(r.max_edits_per_reservation) ? r.max_edits_per_reservation : 0,
      allow_party_size_changes : !!r.allow_party_size_changes,
      allow_switch_service     : !!r.allow_switch_service,
      allow_switch_class       : !!r.allow_switch_class,
      allow_change_room_type   : !!r.allow_change_room_type,
      allow_change_seating_area: !!r.allow_change_seating_area,
    };
    if ((r.scope || 'all') === 'custom') {
      out.applies_to = {
        personal_services: r.applies_to?.personal_services || [],
        table_inventory  : r.applies_to?.table_inventory   || [],
        class_based      : r.applies_to?.class_based       || [],
        accommodation    : r.applies_to?.accommodation     || [],
      };
    }
    return out;
  });
}




const isoToHHMM = (v) => {
  if (!v || typeof v !== "string") return null; // was `return v`
  if (/^\d{2}:\d{2}$/.test(v)) return v;
  try {
    const d = new Date(v);
    const hh = `${d.getHours()}`.padStart(2, "0");
    const mm = `${d.getMinutes()}`.padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return null; // was `return v`
  }
};


/* Recursive walk that:
 *  1. strips artefact keys
 *  2. removes `currency` wherever a `{amount,currency}` object is found
 */
function cleanDeep(node) {
  if (Array.isArray(node)) {
    node.forEach(cleanDeep);
    return;
  }
  if (node && typeof node === "object") {
    // ───── 1. strip unwanted keys
    STRIP_KEYS.forEach((k) => {
      if (k in node) delete node[k];
    });

    // ───── 2. remove .currency if paired with amount
    if ("amount" in node && "currency" in node) {
      delete node.currency;
    }

    // recurse
    Object.values(node).forEach(cleanDeep);
  }
}


/* Convert operational-hours ISO datetimes to HH:MM */
function normaliseHoursRule(rule) {
  if (!rule?.hours) return;

  // short → long weekday keys
  Object.entries(SHORT_TO_LONG_DAY).forEach(([short, long]) => {
    if (short in rule.hours) {
      rule.hours[long] = rule.hours[short];
      delete rule.hours[short];
    }
  });

  const patchSlots = (slots = []) =>
    slots.forEach((s) => {
      s.open = isoToHHMM(s.open);
      s.close = isoToHHMM(s.close);
    });

  // day-of-week blocks
  Object.values(rule.hours).forEach((v) => {
    if (!v || typeof v !== "object") return;
    patchSlots(v.slots);
  });

  // date exceptions
  (rule.hours.exceptions || []).forEach((ex) => patchSlots(ex.slots));
}

/* ------------------------------------------------------------------ */
/* MAIN                                                               */
/* ------------------------------------------------------------------ */

export function sanitizeReservationPayload(raw) {
  // Deep-clone so React state is never mutated
  const data = _cloneDeep(raw);

  /* 1. Global deep-clean */
  cleanDeep(data);

  /* 2. Personal / Modular / Class / Accommodation service blocks ------*/
  SERVICE_MODES.forEach((k) => {
    const block = data[k];
    if (!block) return;

    /* price.currency already stripped by cleanDeep */

    /* class-based: sessions day_of_week → number (1-7) */
    if (k === "class_based") {
      block.classes?.forEach((cls) =>
        cls.sessions?.forEach((s) => {
          if (typeof s.day_of_week === "string") {
            s.day_of_week = LONG_DAY_TO_NUM[s.day_of_week] ?? s.day_of_week;
          }
        })
      );
    }

    if (k === "accommodation") {
            console.log(block);

      block.check_in_after = isoTo12hrTime(block.check_in_after);
      block.check_out_before = isoTo12hrTime(block.check_out_before);
      console.log(block);

      block.room_types?.forEach((rt) => {
        // Only keep image URLs (remove File objects)
        rt.images = rt.images?.filter((img) => typeof img === "string");

        // Remove raw `price` field if still present
        delete rt.price;

        // Clean any nested seasonal pricing
        rt.seasonal_pricing?.forEach((sp) => cleanDeep(sp.price));

        // Normalize modifiers for backend
        if (Array.isArray(rt.modifiers)) {
          const flat = [];

          rt.modifiers.forEach((m) => {
            const mode =
              m.mode === "fixed" ? "fixed_amount" :
                m.mode === "percent" ? "percentage" :
                  MODIFIER_MODE_MAP[m.mode] ?? m.mode;

            const type = MODIFIER_TYPE_MAP[m.type] ?? m.type;

            const base = {
              id: m.id,
              type,
              value: typeof m.value === "number" ? m.value : 0,
              mode: mode ?? "percentage", // fallback to valid default
            };

            if (m.type === "weekday" && Array.isArray(m.param)) {
              m.param.forEach((day) => {
                flat.push({ ...base, param: day });
              });
            } else {
              flat.push({ ...base, param: m.param });
            }
          });

          rt.modifiers = flat;
        }
      });
    }



    if (k === "table_inventory") {
      block.tableTypes?.forEach((tt) => {
        if (tt.turnover_minutes == null) tt.turnover_minutes = 0;
      });
    }
  });

  /* 3. Bundles ------------------------------------------------------- */
  data.bundles?.forEach((b) => {
delete b.fee_policy;
    // normalize window days as integer ≥1
    if (b.consumption_window_days == null || !Number.isInteger(b.consumption_window_days) || b.consumption_window_days < 1) {
      b.consumption_window_days = 1;
    }
  });

  /* 3b. Accommodation: drop dorm_type unless hostel, normalize value */
  if (Array.isArray(data.accommodation?.room_types)) {
    data.accommodation.room_types = data.accommodation.room_types.map((r) => {
      const room = { ...r };
      if (room.is_hostel === true) {
        if (!['mixed', 'female', 'male'].includes(room.dorm_type)) {
          // default to mixed if user didn’t pick yet
          room.dorm_type = 'mixed';
        }
      } else {
        // Joi forbids dorm_type when not a hostel
        delete room.dorm_type;
      }
      return room;
    });
  }

  /* 4. Taxes & fees -------------------------------------------------- */
  if (data.taxes_fees) {
    // Strip helper index and unwanted keys
    data.taxes_fees.taxes?.forEach((t) => {
      delete t.__idx;
      cleanDeep(t); // just in case
    });

    // Remove fees if present (Joi expects them absent)
    delete data.taxes_fees.fees;
  }

  /* 5. Operational hours -------------------------------------------- */
  data.operational_hours_rules?.forEach(normaliseHoursRule);
  delete data.employees;
  if (data.taxes_fees) {
    delete data.taxes_fees.fees;
  }

  // Remove legacy waitlist flag if present; waitlist is now always available for clients
  if ('waitlist_enabled' in data) delete data.waitlist_enabled;

  if (!data.manual_approval_settings) {
    data.manual_approval_settings = {
      apply_to_all: true,
      selected_offerings: {
        personal_services: [],
        table_inventory  : [],
        class_based      : [],
        accommodation    : [],
      },
    };
  } else {
    const settings = data.manual_approval_settings;
    settings.apply_to_all = settings.apply_to_all !== false;
    const selected = settings.selected_offerings || {};
    const normalized = {
      personal_services: [],
      table_inventory  : [],
      class_based      : [],
      accommodation    : [],
    };
    Object.keys(normalized).forEach((mode) => {
      const list = Array.isArray(selected[mode]) ? selected[mode] : [];
      const deduped = Array.from(new Set(list.filter(Boolean)));
      normalized[mode] = deduped;
    });
    settings.selected_offerings = normalized;
  }

  // 7. Booking rules: strip helper idx and ensure scope/applies_to shape
  if (Array.isArray(data.booking_rules)) {
    data.booking_rules = data.booking_rules.map((r) => {
      const copy = { ...r };
      delete copy.__idx;
      copy.scope = copy.scope === 'custom' ? 'custom' : 'all';
      if (copy.scope === 'custom') {
        const norm = { personal_services: [], table_inventory: [], class_based: [], accommodation: [] };
        Object.entries(copy.applies_to || {}).forEach(([k, arr]) => {
          if (k in norm && Array.isArray(arr)) norm[k] = Array.from(new Set(arr.filter(Boolean)));
        });
        copy.applies_to = norm;
      } else {
        delete copy.applies_to;
      }
      copy.cutoff_min = Number(copy.cutoff_min || 0);
      copy.lead_days  = Number(copy.lead_days || 0);
      return copy;
    });
  }

  // 8. Edit rules: normalize
  if (Array.isArray(data.edit_rules)) {
    data.edit_rules = normEditRules(data.edit_rules);
  }

  /* 6. All done */
  return data;
}
